import { Link } from "react-router";
import { urlHome, urlScanProduct } from "../../urls";
import { Button } from "@repo/ui/components/button";
import { useSendEvent } from "../../hooks/use-send-event";
import { useEffect, useState } from "react";
import { playerApi } from "@repo/api-client";
import { useDebounce } from "@repo/ui/hooks/use-debounce";
import type { Player as PlayerType } from "@repo/shared-types";
import { useAsync } from "@repo/ui/hooks/use-async";
import { Keyboard } from "../../components/keyboard";
import { cn } from "@repo/ui/lib/utils";
import { Title } from "../../components/title";
import { useSetAtom } from "jotai";
import { playerAtom, productsAtom } from "../../stores/pos";
import { useProductScan } from "../../hooks/use-product-scan";
import { Container } from "../../components/container";
import { InputText } from "../../components/input-text";

interface AutocompleteItem {
  id: string;
  display: string;
  player: PlayerType;
}

const getPhoneMasked = (phone: string): string => {
  const phoneLength = phone.length;
  let lastNumbersVisible = 3;
  if (phoneLength <= 3) {
    lastNumbersVisible = 1;
  }
  return `${"*".repeat(phoneLength - lastNumbersVisible)}${phone.slice(
    -lastNumbersVisible
  )}`;
};

const getDisplay = (player: PlayerType): string => {
  return `${player.name} ${player.lastName
    .charAt(0)
    .toUpperCase()}. (${getPhoneMasked(player.phone)})`;
};

const getAutocompleteItem = (player: PlayerType): AutocompleteItem => {
  return {
    id: player.id,
    player,
    display: getDisplay(player),
  };
};

function Home() {
  const [name, setName] = useState("");
  const sendEvent = useSendEvent();
  const debouncedName = useDebounce(name, 200);
  const [autocomplete, setAutocomplete] = useState<AutocompleteItem[]>([]);
  const setPlayer = useSetAtom(playerAtom);
  const setProducts = useSetAtom(productsAtom);

  useProductScan("home");

  const { execute: loadAutocomplete } = useAsync(async () => {
    const response = await playerApi.searchPlayer(debouncedName);
    setAutocomplete(response.results.map(getAutocompleteItem));
  });

  useEffect(() => {
    loadAutocomplete();
  }, [debouncedName]);

  useEffect(() => {
    setPlayer(null);
    setProducts([]);
  }, []);

  const continueEnabled = name.length > 2;

  return (
    <Container>
      <Title className="text-2xl!">¿Cuál es tu nombre?</Title>
      <InputText value={name} placeholder="Escribe tu nombre..." />
      <div className="h-8 flex gap-2 my-1 overflow-x-auto no-scrollbar w-full">
        {autocomplete.map((player) => (
          <Link
            key={player.id}
            className="bg-gray-100 rounded-md px-2 py-1 border border-gray-200 text-xs md:text-sm whitespace-nowrap"
            to={urlScanProduct()}
            onClick={() => setPlayer(player.player)}
            viewTransition
          >
            {player.display}
          </Link>
        ))}
      </div>
      <Keyboard onChange={setName} value={name} />

      <Button
        asChild
        className={cn(
          "h-18 text-2xl! font-bold! w-full",
          continueEnabled ? "" : "opacity-50 cursor-not-allowed!"
        )}
        onClick={(e) => {
          if (!continueEnabled) {
            e.preventDefault();
            return;
          }
          sendEvent("enter_name", { name });
          setPlayer(name);
        }}
      >
        <Link to={urlScanProduct()} viewTransition>
          Continuar
        </Link>
      </Button>
    </Container>
  );
}

export const route = {
  path: urlHome(),
  element: <Home />,
  handle: {
    title: "¿Cuál es tu nombre?",
  },
};
